import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchStallPricingConfig,
  saveStallPricingConfig,
  updateIndividualBoothPrice,
  fetchBooths,
  StallPricingConfig as StallPricingConfigType,
  StallCategoryPricing,
  Booth,
} from '../../lib/api';
import {
  IndianRupee,
  Building2,
  CheckCircle2,
  AlertCircle,
  Save,
  RotateCcw,
  RefreshCw,
  Search,
  Check,
  ShieldCheck,
  Sparkles,
  Layers,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';

interface StallPricingConfigProps {
  adminKey: string;
}

export function StallPricingConfig({ adminKey }: StallPricingConfigProps) {
  const queryClient = useQueryClient();

  const [categories, setCategories] = useState<StallCategoryPricing[]>([]);
  const [gstRatePercent, setGstRatePercent] = useState<number>(18);
  const [applyToExisting, setApplyToExisting] = useState<boolean>(true);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Individual stall price editor
  const [searchQuery, setSearchQuery] = useState('');
  const [individualPrices, setIndividualPrices] = useState<Record<string, number>>({});
  const [savingBoothId, setSavingBoothId] = useState<string | null>(null);

  // Query: Fetch category pricing config
  const { data: configData, isLoading: isConfigLoading, refetch: refetchConfig } = useQuery<StallPricingConfigType>({
    queryKey: ['admin-stall-pricing', adminKey],
    queryFn: () => fetchStallPricingConfig(adminKey),
    enabled: !!adminKey,
  });

  // Query: Fetch live booths for individual override table
  const { data: booths = [], isLoading: isBoothsLoading, refetch: refetchBooths } = useQuery<Booth[]>({
    queryKey: ['booths'],
    queryFn: () => fetchBooths(),
  });

  // Sync server config to local form state
  React.useEffect(() => {
    if (configData?.categories) {
      setCategories(configData.categories);
      setGstRatePercent(configData.gstRatePercent ?? 18);
    }
  }, [configData]);

  // Mutation: Save category pricing config
  const saveMutation = useMutation({
    mutationFn: (applyToBooths: boolean) =>
      saveStallPricingConfig(
        {
          categories,
          gstRatePercent,
          currency: 'INR',
          applyToBooths,
        },
        adminKey
      ),
    onSuccess: (data) => {
      setStatusMessage({
        type: 'success',
        text: `Stall pricing configuration updated successfully!${applyToExisting ? ' Rates applied to existing exhibition stalls.' : ''}`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin-stall-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['booths'] });
      setTimeout(() => setStatusMessage(null), 4000);
    },
    onError: (err: any) => {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to save stall pricing' });
    },
  });

  // Mutation: Save individual booth price
  const updateSingleBoothMutation = useMutation({
    mutationFn: ({ boothId, priceInr }: { boothId: string; priceInr: number }) =>
      updateIndividualBoothPrice(boothId, priceInr, adminKey),
    onSuccess: (data, vars) => {
      setSavingBoothId(null);
      setStatusMessage({
        type: 'success',
        text: `Stall ${vars.boothId} price updated to ₹${vars.priceInr.toLocaleString('en-IN')}`,
      });
      queryClient.invalidateQueries({ queryKey: ['booths'] });
      setTimeout(() => setStatusMessage(null), 3500);
    },
    onError: (err: any) => {
      setSavingBoothId(null);
      setStatusMessage({ type: 'error', text: err.message || 'Failed to update stall price' });
    },
  });

  const handleCategoryPriceChange = (index: number, newPrice: number) => {
    setCategories((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], priceInr: Math.max(0, newPrice) };
      return next;
    });
  };

  const handleInclusionChange = (catIdx: number, incIdx: number, val: string) => {
    setCategories((prev) => {
      const next = [...prev];
      const inclusions = [...next[catIdx].inclusions];
      inclusions[incIdx] = val;
      next[catIdx] = { ...next[catIdx], inclusions };
      return next;
    });
  };

  const handleAddInclusion = (catIdx: number) => {
    setCategories((prev) => {
      const next = [...prev];
      next[catIdx] = {
        ...next[catIdx],
        inclusions: [...next[catIdx].inclusions, 'New deliverable item'],
      };
      return next;
    });
  };

  const handleRemoveInclusion = (catIdx: number, incIdx: number) => {
    setCategories((prev) => {
      const next = [...prev];
      next[catIdx] = {
        ...next[catIdx],
        inclusions: next[catIdx].inclusions.filter((_, i) => i !== incIdx),
      };
      return next;
    });
  };

  const handleSaveIndividualBooth = (booth: Booth) => {
    const customPrice = individualPrices[booth.id] ?? booth.priceInr;
    setSavingBoothId(booth.id);
    updateSingleBoothMutation.mutate({ boothId: booth.id, priceInr: customPrice });
  };

  const filteredBooths = booths.filter((b) => {
    return (
      b.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.companyName && b.companyName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      b.dimensions.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <div className="space-y-8 animate-in fade-in">
      {/* Top Banner & Header */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
        <div>
          <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#0A3D27]" />
            <span>Exhibition Stall Commercial Pricing & Deliverables</span>
          </h3>
          <p className="text-xs text-slate-600 mt-0.5">
            Configure standard base rates per stall classification, 18% GST calculation, and custom individual stall overrides.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-[#F8F9F6] border border-slate-300 px-3 py-1.5 rounded-xl">
            <span className="text-xs text-slate-600 font-semibold">Statutory GST:</span>
            <input
              type="number"
              min={0}
              max={28}
              value={gstRatePercent}
              onChange={(e) => setGstRatePercent(Number(e.target.value))}
              className="w-12 bg-white border border-slate-300 text-xs font-bold text-[#0A3D27] rounded px-1.5 py-0.5 text-center focus:outline-none focus:border-[#0A3D27]"
            />
            <span className="text-xs font-bold text-slate-600">%</span>
          </div>

          <button
            type="button"
            onClick={() => saveMutation.mutate(applyToExisting)}
            disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-xs transition-all shadow-sm shadow-[#0A3D27]/20 cursor-pointer disabled:opacity-50"
          >
            <Save className={`w-3.5 h-3.5 ${saveMutation.isPending ? 'animate-spin' : ''}`} />
            <span>{saveMutation.isPending ? 'Saving...' : 'Save Stall Pricing'}</span>
          </button>
        </div>
      </div>

      {statusMessage && (
        <div
          className={`p-3.5 rounded-xl text-xs flex items-center justify-between animate-in fade-in shadow-xs ${
            statusMessage.type === 'success'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border border-rose-200 text-rose-800'
          }`}
        >
          <div className="flex items-center gap-2 font-semibold">
            {statusMessage.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span>{statusMessage.text}</span>
          </div>
          <button onClick={() => setStatusMessage(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            ✕
          </button>
        </div>
      )}

      {/* SECTION 1: CATEGORY BASE PRICING CARDS */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#0A3D27]" />
              <span>Standard Stall Categories & Inclusions</span>
            </h4>
            <p className="text-xs text-slate-600 mt-0.5">
              Base catalog price per stall category. These rates apply to newly locked stalls and dynamic pricing displays.
            </p>
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={applyToExisting}
              onChange={(e) => setApplyToExisting(e.target.checked)}
              className="rounded bg-[#F8F9F6] border-slate-300 text-[#0A3D27] focus:ring-0"
            />
            <span className="font-semibold">Auto-update all existing available stalls in database</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {categories.map((cat, idx) => {
            const basePrice = Number(cat.priceInr) || 0;
            const gstAmount = Math.round((basePrice * gstRatePercent) / 100);
            const grossTotal = basePrice + gstAmount;

            return (
              <div
                key={cat.category}
                className="bg-white border border-slate-200/90 rounded-3xl p-5 space-y-4 shadow-xs hover:border-slate-300 transition-all flex flex-col justify-between"
              >
                <div className="space-y-3">
                  {/* Category Header */}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#0A3D27]/10 text-[#0A3D27] border border-[#0A3D27]/20">
                      {cat.dimensions} • {cat.areaSqm} sqm
                    </span>
                    <span className="text-xs font-mono text-slate-500">₹/sqm: {Math.round(basePrice / (cat.areaSqm || 1)).toLocaleString('en-IN')}</span>
                  </div>

                  <div>
                    <h5 className="text-base font-bold text-slate-900">{cat.name}</h5>
                    <p className="text-[11px] text-slate-500">Modular German Octanorm prefabricated stall</p>
                  </div>

                  {/* Pricing Inputs */}
                  <div className="bg-[#F8F9F6] p-3.5 rounded-2xl border border-slate-200/90 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-bold text-slate-600">Base Price (INR):</label>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold text-slate-500">₹</span>
                        <input
                          type="number"
                          step={1000}
                          value={cat.priceInr}
                          onChange={(e) => handleCategoryPriceChange(idx, Number(e.target.value))}
                          className="w-28 bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-mono font-bold text-[#0A3D27] text-right focus:outline-none focus:border-[#0A3D27]"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-600 pt-1 border-t border-slate-200">
                      <span>+ {gstRatePercent}% GST:</span>
                      <span className="font-mono text-slate-800">₹{gstAmount.toLocaleString('en-IN')}</span>
                    </div>

                    <div className="flex items-center justify-between text-xs font-bold text-slate-900 pt-1 border-t border-slate-200">
                      <span>Gross Invoiced:</span>
                      <span className="font-mono text-[#0A3D27] font-extrabold">₹{grossTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  {/* Standard Deliverables & Inclusions List */}
                  <div className="space-y-1.5 pt-1">
                    <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 uppercase">
                      <span>Deliverables</span>
                      <button
                        type="button"
                        onClick={() => handleAddInclusion(idx)}
                        className="text-[#0A3D27] hover:text-[#062B1B] font-semibold text-[10px] cursor-pointer"
                      >
                        + Add Item
                      </button>
                    </div>

                    <ul className="space-y-1.5 text-xs text-slate-700">
                      {cat.inclusions.map((inc, iIdx) => (
                        <li key={iIdx} className="flex items-center gap-1.5 group">
                          <Check className="w-3.5 h-3.5 text-[#0A3D27] shrink-0" />
                          <input
                            type="text"
                            value={inc}
                            onChange={(e) => handleInclusionChange(idx, iIdx, e.target.value)}
                            className="bg-transparent text-slate-800 text-xs w-full focus:outline-none focus:bg-white focus:border-slate-300 border border-transparent px-1 py-0.5 rounded"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveInclusion(idx, iIdx)}
                            className="text-slate-400 hover:text-rose-600 text-xs opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer shrink-0"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SECTION 2: INDIVIDUAL STALL PRICE OVERRIDES TABLE */}
      <div className="bg-white border border-slate-200/90 rounded-3xl p-5 sm:p-6 space-y-4 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#0A3D27]" />
              <span>Individual Stall Custom Price Overrides</span>
            </h4>
            <p className="text-xs text-slate-600 mt-0.5">
              Set custom price premiums on specific high-traffic corner stalls (e.g. entrance-facing PB01 or GB01).
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search stall ID or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] w-56"
            />
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200 max-h-96">
          <table className="w-full text-left text-xs text-slate-700">
            <thead className="bg-slate-50 text-slate-600 font-bold uppercase text-[10px] tracking-wider sticky top-0 border-b border-slate-200">
              <tr>
                <th className="py-3 px-4">Stall ID</th>
                <th className="py-3 px-4">Size & Area</th>
                <th className="py-3 px-4">Allocated Company</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Base Rate (₹)</th>
                <th className="py-3 px-4">+18% GST Gross</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {filteredBooths.map((b) => {
                const currentPrice = individualPrices[b.id] ?? b.priceInr;
                const isModified = individualPrices[b.id] !== undefined && individualPrices[b.id] !== b.priceInr;
                const isSaving = savingBoothId === b.id;

                return (
                  <tr key={b.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-2.5 px-4 font-mono font-bold text-slate-900">{b.id}</td>
                    <td className="py-2.5 px-4">{b.dimensions} ({b.areaSqm} sqm)</td>
                    <td className="py-2.5 px-4 text-slate-700">
                      {b.companyName ? (
                        <span className="font-semibold text-[#0A3D27]">{b.companyName}</span>
                      ) : (
                        <span className="text-slate-400 italic">Unallocated</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          b.status === 'booked'
                            ? 'bg-rose-50 text-rose-700 border border-rose-200'
                            : b.status === 'on_hold'
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        }`}
                      >
                        {b.status}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-1 font-mono">
                        <span className="text-slate-400">₹</span>
                        <input
                          type="number"
                          step={1000}
                          value={currentPrice}
                          onChange={(e) =>
                            setIndividualPrices((prev) => ({
                              ...prev,
                              [b.id]: Number(e.target.value),
                            }))
                          }
                          className={`w-28 bg-white border rounded-lg px-2 py-1 text-xs font-mono font-bold text-right focus:outline-none ${
                            isModified ? 'border-[#C5A059] text-[#785613]' : 'border-slate-300 text-[#0A3D27]'
                          }`}
                        />
                      </div>
                    </td>
                    <td className="py-2.5 px-4 font-mono text-slate-600">
                      ₹{Math.round(currentPrice * 1.18).toLocaleString('en-IN')}
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      {isModified && (
                        <button
                          type="button"
                          onClick={() => handleSaveIndividualBooth(b)}
                          disabled={isSaving}
                          className="px-2.5 py-1 bg-[#C5A059] hover:bg-[#b58f47] text-white rounded-lg text-xs font-bold transition-all cursor-pointer shadow-xs disabled:opacity-50"
                        >
                          {isSaving ? 'Updating...' : 'Save Override'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
